import React, { useEffect } from 'react';

import { Typography } from '@mui/material';
import { useAutofillCurrency } from 'hooks/useAutofillCurrency';
import { useProviderCurrencyIds } from 'hooks/useProviderCurrencyIds';
import { CommissionsNameInput } from 'pages/Commissions/components/CommissionsNameInput';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RaRecord, required, useRecordContext } from 'react-admin';
import { FieldValues, useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import {
  AutocompleteInput,
  BooleanInput,
  WatchedTextInput,
  AutocompleteMultiselectInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { CommissionsCalculationMethodInput } from 'shared/widgets/CommissionsCalculationMethodInput/CommissionsCalculationMethodInput';
import { useCalculationMethodVisibility } from 'shared/widgets/CommissionsCalculationMethodInput/hooks/useCalculationMethodVisibility';
import { useResetCalculationMethodOnHide } from 'shared/widgets/CommissionsCalculationMethodInput/hooks/useResetCalculationMethodOnHide';
import { Commission } from 'types/embedded/commission';
import { cleanEmpty } from 'utils/cleanEmpty';

import { BoxAdditionalStyled, BoxMainStyled } from './MerchantShowCommissionsForm.styled';

type Props = {
  errors: string[];
  merchantId: string | undefined;
  children?: React.ReactNode;
};

export interface CommissionsFormValues extends FieldValues {
  name: string;
  merchant: string;
  currency: string;
  provider: string;
  transactionTypes: string[];
  gateway: string;
  rate: {
    zeroed: boolean;
    percent: string | null;
    fix: string | null;
    min: string | null;
    max: string | null;
  };
  calculationMethod?: string | null;
}

export const MerchantShowCommissionsForm: React.FC<Props> = ({ errors, merchantId, children }) => {
  const translate = useTypedTranslate();
  const { watch, trigger, setError, setValue } = useFormContext();
  const zeroed = watch('rate.zeroed');
  const provider = watch('provider');

  const currencyIds = useProviderCurrencyIds(provider);
  useAutofillCurrency(currencyIds);
  const isCalculationMethodVisible = useCalculationMethodVisibility();
  useResetCalculationMethodOnHide(isCalculationMethodVisible);

  const record = useRecordContext<Commission>();

  useEffect(() => {
    errors.forEach((error: string) => {
      const [field, message] = error.split(':');
      setError(field, { type: 'custom', message });
    });
  }, [errors]);

  const triggerPercentValidation = () =>
    trigger(['rate.percent', 'rate.fix', 'rate.max', 'rate.min']);

  return (
    <Box sx={{ padding: '24px 24px 0', maxHeight: '650px', overflowY: 'auto' }}>
      <Typography fontWeight={500} variant="body1">
        {translate('app.merchantCommissions.basicSettings')}
      </Typography>
      <BoxMainStyled>
        <CommissionsNameInput
          fullWidth
          label={translate('app.table.name')}
          merchantId={merchantId}
          size="small"
          source="name"
          sx={{ gridColumn: ' 1 / span 3' }}
          validate={required()}
          variant="outlined"
        />
        <ReferenceInput
          filter={{
            merchantAccounts: {
              merchants: [merchantId],
            },
          }}
          perPage={100}
          reference="providers"
          source="provider"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            isRequired
            label={translate('app.table.provider')}
            onChange={() => {
              setValue('currency', '');
            }}
            optionText="name"
            readOnly={!!record?.parent}
            size="small"
            source="provider"
            variant="outlined"
          />
        </ReferenceInput>
        <ReferenceArrayInput
          perPage={100}
          reference="transaction_types/commission/list"
          source="transactionTypes"
        >
          <AutocompleteMultiselectInput
            isRequired
            label={translate('app.commissions.transactionTypes')}
            name="transactionTypes"
            optionText="name"
            readOnly={!!record?.parent}
            size="small"
            sx={{
              '.MuiTextField-root': {
                marginTop: 0,
              },
            }}
          />
        </ReferenceArrayInput>
        <ReferenceInput
          filter={cleanEmpty({ id: currencyIds.length ? currencyIds : undefined, active: true })}
          perPage={100}
          reference="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteInput
            disabled={!provider}
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            fullWidth
            isRequired
            label={translate('app.table.currency')}
            optionText={(record: RaRecord) => `${record?.alphaCode}, ${record?.name}`}
            readOnly={!!record?.parent}
            size="small"
            variant="outlined"
          />
        </ReferenceInput>
      </BoxMainStyled>
      {children}
      <Box alignItems="center" display="flex" justifyContent="space-between" width="100%">
        <Typography fontWeight={500} variant="body1">
          {translate('app.merchantCommissions.feeConditions')}
        </Typography>
        <BooleanInput
          label={translate('app.commissions.zeroed')}
          size="small"
          source="rate.zeroed"
          variant="outlined"
        />
      </Box>
      <BoxAdditionalStyled>
        <WatchedTextInput
          defaultValue={null}
          disabled={zeroed}
          fullWidth
          label={translate('app.commissions.percent')}
          onChange={triggerPercentValidation}
          size="small"
          source="rate.percent"
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          defaultValue={null}
          disabled={zeroed}
          fullWidth
          label={translate('app.commissions.fixed')}
          onChange={triggerPercentValidation}
          size="small"
          source="rate.fix"
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          defaultValue={null}
          disabled={zeroed}
          fullWidth
          label={translate('app.commissions.max')}
          onChange={triggerPercentValidation}
          size="small"
          source="rate.max"
          variant="outlined"
          watchField="rate.zeroed"
        />
        <WatchedTextInput
          defaultValue={null}
          disabled={zeroed}
          fullWidth
          label={translate('app.commissions.min')}
          onChange={triggerPercentValidation}
          size="small"
          source="rate.min"
          variant="outlined"
          watchField="rate.zeroed"
        />
      </BoxAdditionalStyled>
      {isCalculationMethodVisible && (
        <Box sx={{ mt: '10px' }}>
          <CommissionsCalculationMethodInput />
        </Box>
      )}
    </Box>
  );
};
